"use client";

import React, { useMemo } from "react";
import { useTranslations } from "next-intl";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { cn } from "@/src/lib/utils";
import NumericText from "@/components/NumericText";
import { formatDate } from "@/shared/dateFormat";
import type {
  ReportQuizComponent,
  ReportResult,
  ReportStudentAssessment,
} from "@/types/endpoints/reports/GetAssignmentReportData";
import type {
  GetAssignmentReportResultsResponse,
  ReportResultItem,
} from "@/types/endpoints/reports/GetAssignmentReportResults";
import type { ReportCertificate } from "@/types/endpoints/reports/GetAssignmentReportInfo";

interface PublicResultsTabProps {
  quizComponents?: ReportQuizComponent[];
  /** Legacy results from /assignment.json */
  results?: ReportResult[];
  /** New results payload from /results.json */
  resultsData?: GetAssignmentReportResultsResponse;
  studentAssessments?: ReportStudentAssessment[];
  totalPoints?: number;
  isPointSystemEnabled?: boolean;
  /** Assessment name from reportData (e.g. "Start MBA") */
  assessmentName?: string;
  certificate?: ReportCertificate | null;
}

type QuestionStatus = "empty" | "correct" | "wrong";

/**
 * Determine question status based on result code
 * Based on backend behavior:
 * - result: 3 => correct (usually has points)
 * - result: 2 => wrong (often points=null)
 * - result: 1 => also correct in some contexts
 * - result: 0 => empty/not answered
 */
function getQuestionStatusFromResult(result: ReportResultItem): QuestionStatus {
  // result: 3 = correct, result: 1 = also correct
  if (result.result === 3 || result.result === 1) return "correct";
  // result: 2 = wrong
  if (result.result === 2) return "wrong";
  // result: 0 or other = empty
  return "empty";
}

/**
 * Determine question status based on result data (legacy fallback)
 */
function getQuestionStatus(
  componentId: number,
  results?: ReportResult[],
  resultsItems?: ReportResultItem[]
): QuestionStatus {
  // Prefer new /results.json payload when available
  if (resultsItems && resultsItems.length > 0) {
    const item = resultsItems.find((r) => r.component_id === componentId);
    if (!item) return "empty";
    return getQuestionStatusFromResult(item);
  }

  // Fallback to legacy /assignment.json results
  if (!results) return "empty";

  const result = results.find((r) => r.component_id === componentId);

  if (!result) return "empty";

  // Check is_correct first
  if (result.is_correct === true) return "correct";
  if (result.is_correct === false) return "wrong";

  // Fallback to score check
  if (result.score !== undefined && result.max_score !== undefined) {
    if (result.score > 0) return "correct";
    if (result.score === 0) return "wrong";
  }

  return "empty";
}

/**
 * QuestionStatusBlock - displays a single question status
 */
function QuestionStatusBlock({
  id,
  status,
}: {
  id: number;
  status: QuestionStatus;
}) {
  return (
    <NumericText
      className={cn(
        "flex items-center justify-center rounded-md border text-sm font-medium aspect-square w-full select-none transition bg-border-muted",
        status === "correct" && "bg-[#19A44B] text-white border-[#19A44B]",
        status === "wrong" && "bg-[#D15B46] text-white border-[#D15B46]",
        status === "empty" && "bg-white text-muted-foreground border-border"
      )}
    >
      {id}
    </NumericText>
  );
}

/**
 * Group results by assessment_id from /results.json response
 */
interface AssessmentGroup {
  assessmentId: number;
  assessmentName: string;
  results: ReportResultItem[];
}

/**
 * PublicResultsTab - displays quiz results for public report page
 * Shows question statuses grouped by assessments (if available)
 */
export default function PublicResultsTab({
  quizComponents,
  results,
  resultsData,
  studentAssessments,
  totalPoints,
  isPointSystemEnabled,
  assessmentName,
  certificate,
}: PublicResultsTabProps) {
  const t = useTranslations();

  const isCertificateActive = certificate?.is_active !== 0;
  const certificateStatusText = isCertificateActive
    ? t("label-certificate-issued")
    : t("label-certificate-revoked");
  const certificateStatusClass = isCertificateActive
    ? "text-[#19A44B]"
    : "text-muted-foreground";
  const certificateIssuedAt = certificate?.issued_at
    ? formatDate(certificate.issued_at, "ru")
    : "";

  const resultsItems = resultsData?.entities?.data;

  // Group results by assessment_id from /results.json
  const assessmentGroupsFromResults = useMemo<AssessmentGroup[]>(() => {
    if (!resultsItems || resultsItems.length === 0) {
      return [];
    }

    // Group by assessment_id
    const groupMap = new Map<number, ReportResultItem[]>();
    
    for (const item of resultsItems) {
      const existing = groupMap.get(item.assessment_id);
      if (existing) {
        existing.push(item);
      } else {
        groupMap.set(item.assessment_id, [item]);
      }
    }

    // Convert to array and find assessment names
    const groups: AssessmentGroup[] = [];
    
    for (const [assessmentId, items] of groupMap) {
      // Try to find assessment name from studentAssessments
      // Check both component_id and assessment_id fields
      const studentAssessment = studentAssessments?.find(
        (sa) => 
          sa.component_id === assessmentId || 
          sa.assessment_id === assessmentId
      );
      
      // Try multiple possible name fields
      const name = 
        studentAssessment?.assessment_name || 
        studentAssessment?.assessment || 
        assessmentName || 
        `Assessment #${assessmentId}`;
      
      groups.push({
        assessmentId,
        assessmentName: name,
        results: items,
      });
    }

    return groups;
  }, [resultsItems, studentAssessments, assessmentName]);

  // Calculate question statuses for flat view (no assessments) - legacy fallback
  const questionStatuses = useMemo(() => {
    if (!quizComponents) return [];

    return quizComponents.map((component, index) => ({
      id: index + 1,
      componentId: component.id,
      status: getQuestionStatus(component.id, results, resultsItems),
    }));
  }, [quizComponents, results, resultsItems]);

  // Check if we have any data to display
  const hasResultsData = resultsItems && resultsItems.length > 0;
  const hasQuizComponents = quizComponents && quizComponents.length > 0;

  if (!hasResultsData && !hasQuizComponents) {
    return (
      <div className="p-6 text-sm text-muted-foreground text-center">
        {t("label-empty-found")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Total score */}
      {(isPointSystemEnabled || totalPoints !== undefined) && (
        <Card className="shadow-none">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              {t("label-assignment-sum-results")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-10 w-10 items-center justify-center border rounded-md text-xl font-semibold text-blue-600">
                  {totalPoints ?? 0}
                </span>
                <span className="text-sm text-muted-foreground">
                  {t("label-points-alt")}
                </span>
              </div>
              {certificate && (
                <div className="flex flex-col gap-1">
                  <span className={`text-sm font-semibold ${certificateStatusClass}`}>
                    {certificateStatusText}
                  </span>
                  {certificateIssuedAt && (
                    <span className="text-xs text-muted-foreground">
                      {certificateIssuedAt}
                    </span>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Primary view: Group by assessment from /results.json */}
      {assessmentGroupsFromResults.length > 0 ? (
        assessmentGroupsFromResults.map((group) => (
          <Card key={group.assessmentId} className="border shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {group.assessmentName}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-11 gap-2">
                {group.results.map((item, index) => (
                  <QuestionStatusBlock
                    key={item.id}
                    id={index + 1}
                    status={getQuestionStatusFromResult(item)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        ))
      ) : hasQuizComponents ? (
        // Fallback: flat view using quiz_components
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">
              {assessmentName || t("label-assignment-results")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-11 gap-2 mt-2">
              {questionStatuses.map((q) => (
                <QuestionStatusBlock
                  key={q.componentId}
                  id={q.id}
                  status={q.status}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      
    </div>
  );
}
